// Global Instructions Rule Applied!
//
// Tranche form HTTP calls. One read: GET /tranches/:id → view__tranche_detail
// (facts + current position + chronological history), validated against the
// strict tranche detail model before it reaches the component.
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  TrancheDetailPayload,
  validateTrancheDetailPayload
} from '../../../state_manager/data_models/tranche_detail_model.ts'
import type { FilterValue, SortConfig } from '../../global_configs/tableViewPreferences.ts'
import type { TranchesPaginatedResponse } from '../../../state_manager/state_update_actions/tranches/index.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

const authHeaders = (accessToken: string | null): Record<string, string> => ({
  'Content-Type': 'application/json',
  ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
})

export const fetchTranchesFiltered = async (
  sorting: SortConfig[] = [],
  filters: FilterValue[] = [],
  page: number = 1,
  pageSize: number | null = null,
  accessToken: string | null = null
): Promise<TranchesPaginatedResponse> => {
  try {
    const requestBody = {
      data: {
        page,
        page_size: pageSize ?? 200,
        sorting: sorting.map((s) => ({ key: s.key, direction: s.direction })),
        filters: filters.map((f) => ({ key: f.key, value: f.value }))
      }
    }
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.ASSET_TRANSACTIONS.TRANCHES_FILTER), {
      method: API_ROUTES.ASSET_TRANSACTIONS.TRANCHES_FILTER.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify(requestBody)
    })
    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch tranches: ${response.status} ${response.statusText}`)
    }
    const json = (await response.json()) as { data?: unknown[]; has_more?: boolean; count?: number }
    const rows = Array.isArray(json.data) ? (json.data as Record<string, unknown>[]) : []
    return {
      data: rows,
      hasMore: Boolean(json.has_more),
      totalCount: json.count ?? rows.length
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching tranches:', error)
    return {
      data: [],
      hasMore: false,
      totalCount: 0,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export const fetchTranchesFilterOptions = async (
  accessToken: string | null
): Promise<{ data?: Record<string, unknown>; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.ASSET_TRANSACTIONS.TRANCHES_FILTER_OPTIONS),
      {
        method: API_ROUTES.ASSET_TRANSACTIONS.TRANCHES_FILTER_OPTIONS.method,
        headers: authHeaders(accessToken)
      }
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `Tranche filter options request failed (${response.status})`)
    }
    const body = await response.json()
    return { data: body.data || {} }
  } catch (e) {
    console.error('fetchTranchesFilterOptions failed:', e)
    return { error: e instanceof Error ? e.message : String(e) }
  }
}

export const fetchTrancheDetail = async (
  accessToken: string | null,
  trancheId: string
): Promise<{ data: TrancheDetailPayload | null; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.ASSET_TRANSACTIONS.TRANCHE_DETAIL, { id: trancheId }, {}),
      {
        method: API_ROUTES.ASSET_TRANSACTIONS.TRANCHE_DETAIL.method,
        headers: authHeaders(accessToken)
      }
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `Tranche request failed (${response.status})`)
    }
    const body = await response.json()
    const payload = validateTrancheDetailPayload(body.data || {})
    return { data: payload }
  } catch (e) {
    console.error('fetchTrancheDetail failed:', e)
    return { data: null, error: e instanceof Error ? e.message : String(e) }
  }
}
